<div class="tw-w-full tw-flex tw-flex-col">
    <!-- Top: View-Tabs + Action-Button -->
    <div class="tw-flex tw-w-full tw-justify-center tw-items-center tw-my-5">
        <!-- Placeholder div -->
        <div class="tw-flex-1"></div>

        <!-- Center: Tab Buttons -->
        <div class="tw-flex-1 tw-flex tw-items-center">
            @if (shouldShowViewToggle()) {
                <div class="tw-flex tw-items-center">
                    <mat-button-toggle-group
                        [value]="currentViewType()"
                        (change)="onViewTypeChange($event)">
                        @for (view of availableViews(); track view.id) {
                            <mat-button-toggle
                                [value]="view.type"
                                [disabled]="!view.enabled"
                                [aria-label]="view.name + '-button'"
                                class="tw-flex tw-items-center">
                                <span>{{
                                    view.translationKey | translate
                                }}</span>
                                @if (
                                    view.type === 'notifications' &&
                                    hasNewNotifications
                                ) {
                                    <mat-icon
                                        color="primary"
                                        fontSet="material-symbols-outlined"
                                        class="bell-shake-animation tw-ml-2">
                                        notifications_active
                                    </mat-icon>
                                }
                            </mat-button-toggle>
                        }
                    </mat-button-toggle-group>
                </div>
            }
        </div>

        <!-- Right Side: Header Actions -->
        <div class="tw-flex-1 tw-flex tw-items-center tw-justify-end tw-mr-4">
            @if (headerActions() && headerActions().length > 0) {
                <div class="header-actions tw-flex tw-items-center tw-gap-2">
                    @for (action of headerActions(); track action.id) {
                        <button
                            mat-raised-button
                            [color]="action.color || 'primary'"
                            [disabled]="isHeaderActionDisabled(action.id)"
                            [style.display]="
                                isHeaderActionVisible(action.id)
                                    ? 'inline-block'
                                    : 'none'
                            "
                            [matTooltip]="
                                action.tooltip
                                    ? (action.tooltip | translate)
                                    : ''
                            "
                            class="!tw-mb-0"
                            [class]="
                                (action.cssClasses ||
                                    'tw-h-12 xl:!tw-text-base') +
                                ' tw-flex tw-items-center tw-justify-center'
                            "
                            [attr.aria-label]="action.id + '-button'"
                            (click)="onHeaderActionClick(action.id)">
                            <!-- Icon -->
                            @if (action.icon) {
                                <mat-icon
                                    fontSet="material-symbols-outlined"
                                    matPrefix>
                                    {{ action.icon }}
                                </mat-icon>
                            }
                            @if (action.svgIcon) {
                                <mat-icon
                                    matPrefix
                                    [svgIcon]="action.svgIcon">
                                </mat-icon>
                            }
                            <!-- Label -->
                            <span>{{ action.translationKey | translate }}</span>
                        </button>
                    }
                </div>
            }
        </div>
    </div>

    <!-- Bot: Search-Bar + Filter -->
    <div class="tw-flex tw-w-full tw-items-center tw-gap-2 tw-bg-[#f6f6f6]">
        <!-- Search-Bar -->
        @if (searchVisible()) {
            <mat-form-field
                appearance="fill"
                subscriptSizing="dynamic"
                class="filter-search-field">
                <mat-label>{{ 'OUTGOING-GOODS.SEARCH' | translate }}</mat-label>
                @if (!searchText()) {
                    <mat-icon
                        matPrefix
                        fontSet="material-symbols-outlined"
                        >search</mat-icon
                    >
                }
                @if (searchText()) {
                    <mat-icon
                        aria-label="clear-filter"
                        class="tw-cursor-pointer"
                        matPrefix
                        (click)="resetFilter()"
                        fontSet="material-symbols-outlined"
                        >delete</mat-icon
                    >
                }
                <input
                    matInput
                    #filterSearch
                    [value]="searchText()"
                    (input)="onSearchTextChange($event)"
                    [readonly]="loading()"
                    autocomplete="off" />
            </mat-form-field>
        }

        <!--
            Active column filters. The chips are the single source of truth for
            what is filtered - a filter stays active even when its column is
            hidden via Show/Hide Columns. The chip-set scrolls after two rows;
            the clear-all button sits outside it so it stays reachable.
        -->
        @if (columnFilters().length > 0) {
            <div
                class="column-filter-chips tw-flex tw-items-center tw-gap-1"
                data-testid="column-filter-chips">
                <mat-chip-set>
                    @for (filter of columnFilters(); track filter.keyName) {
                        <mat-chip
                            [attr.data-testid]="'filter-chip-' + filter.keyName"
                            (removed)="removeColumnFilter(filter.keyName)">
                            <span class="column-filter-chips__label">
                                {{ filter.label | translate }}:
                                {{ chipValue(filter) }}
                            </span>
                            <button
                                matChipRemove
                                [attr.data-testid]="
                                    'filter-chip-remove-' + filter.keyName
                                "
                                [attr.aria-label]="
                                    'TABLE.CLEAR-FILTER' | translate
                                ">
                                <mat-icon fontSet="material-symbols-outlined">
                                    close
                                </mat-icon>
                            </button>
                        </mat-chip>
                    }
                </mat-chip-set>
                @if (columnFilters().length > 1) {
                    <button
                        mat-button
                        class="column-filter-chips__clear-all"
                        data-testid="clear-all-column-filters"
                        (click)="clearColumnFilters()">
                        {{ 'GENERAL.CLEAR-ALL-FILTERS' | translate }} ({{
                            columnFilters().length
                        }})
                    </button>
                }
            </div>
        }

        <div class="tw-flex tw-items-center tw-shrink-0">
            <!--
                Show Closed Toggle. `displayClosedToggle` is additionally false
                for the addons custom views (ECMR, bundle groups), whose rows
                have no "closed" state to filter on.
            -->
            @if (showClosedToggleVisible() && displayClosedToggle()) {
                <mat-checkbox
                    aria-label="show-closed-label"
                    [checked]="showClosed()"
                    (change)="onShowClosedChange($event.checked)"
                    [disabled]="loading()"
                    class="show-closed-checkbox">
                    @if (showClosedLabel()) {
                        <span>{{ showClosedLabel() | translate }}</span>
                    }
                    @if (!showClosedLabel()) {
                        <span>
                            {{ 'OUTGOING-GOODS.SHOW-CLOSED' | translate }}
                        </span>
                    }
                </mat-checkbox>
            }

            <!-- Sites-Filter -->
            @if (shouldShowLocationFilter()) {
                <button
                    mat-button
                    class="tw-mr-1"
                    aria-label="filter-sites-button"
                    [ngClass]="
                        isFilteringLocations()
                            ? 'tw-text-red-400'
                            : 'tw-text-black'
                    "
                    (click)="onLocationFilterClick()"
                    [disabled]="loading()">
                    {{ 'GENERAL.FILTER-SITES' | translate }}
                    @if (getFilteredLocationsCount() > 0) {
                        <span>({{ getFilteredLocationsCount() }})</span>
                    }
                </button>
            }

            <!-- Displayed columns filter -->
            @if (shouldShowColumnFilter()) {
                <button
                    mat-button
                    (click)="onColumnFilterClick()"
                    [disabled]="loading()">
                    {{ 'GENERAL.SHOW-HIDE-COLUMNS' | translate }}
                    @if (filteredColumnsCount() > 0) {
                        <span>({{ filteredColumnsCount() }})</span>
                    }
                </button>
            }
        </div>
    </div>
</div>
